// Ported from dev-scrips-pm-react src/scheduling/services/use-create-encounter.ts (PROD-1817)
// Replaces PA's BOUNDED STUB (a no-op mutation that rejected if fired), which would have
// made the ported Vitals tab render but fail the moment it needed an encounter created.
// NOTE: PA also owns src/encounter/services/use-encounter.ts#useCreateEncounter with a
// different signature (it takes a nav createContext). The old stub's TODO said encounter
// creation belongs to the encounter module — that consolidation is still worth doing;
// this unblocks Vitals without pretending the duplication is gone.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postText } from '@/shared/api/api-client';
import type { AppointmentResponse } from '@/types/scheduling-api';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';

/**
 * POST /api/Patients/CreateEncounter
 *
 * Creates a new clinical encounter scoped to an appointment. Used by
 * the Docs tab Add-document flow (PROD-797 PR 6) when no encounter
 * exists yet for the appointment — Flutter parity:
 * `docs_tab.dart` builds an `EncounterModel` from the appointment
 * context and fires `CreateEncounterEvent` on the first Add click.
 *
 * Wire shape dev-curl verified 2026-05-15:
 *
 * Body — every nullable .NET field ships present-and-null (NOT omitted)
 * to satisfy the binder:
 * ```json
 * {
 *   "identifier": null,
 *   "status": "Initial",
 *   "class": null, "type": null, "serviceType": null,
 *   "subject": "<patientId>",                  // GUID
 *   "basedOn": null,
 *   "appointment": "<appointmentId>",          // GUID
 *   "startDate": null, "endDate": null, "createdDate": null,
 *   "duration": 0,                              // number, NOT null
 *   "account": null,
 *   "serviceProvider": "<providerId>",          // GUID
 *   "parentEncounter": null,
 *   "location": null,                           // location GUID (null on create)
 *   "locationName": "<location.name>",          // human-readable string
 *   "isDeleted": false,                         // bool, NOT null
 *   "reasonForVisit": "<reasonCode.code>",      // GUID
 *   "reasonForVisitName": "<reasonCode.display>", // string
 *   "statusHistoryClass": [],                   // empty array, NOT null
 *   "historyClass": [],
 *   "encounterLocationClass": [],
 *   "speciality": null
 * }
 * ```
 *
 * Response: bare string (encounter id), e.g.
 * `"b01703a7-df47-45eb-28cd-08deb1df76b1"`. NOT JSON-wrapped — use
 * `postText` so the api-client doesn't try to JSON.parse the id.
 * Defensive surrounding-quotes strip handles the JSON-string-literal
 * case too.
 *
 * `status: "Initial"` is the verbatim `AppConstants.encounterStatusInitial`
 * constant from Flutter `app_constants.dart`. DO NOT paraphrase — the
 * backend likely uses it for state-machine routing.
 *
 * On success, invalidates `['encounter-by-appointment', appointmentId]`
 * so any consumer of `useEncounterByAppointment` (Docs tab, future
 * Vitals tab) refetches and sees the new encounter id.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 6 — encounter create-on-demand)
 */

interface CreateEncounterVariables {
  appointment: AppointmentResponse;
}

interface CreateEncounterResponse {
  identifier: string;
}

export const ENCOUNTER_STATUS_INITIAL = 'Initial';

export function useCreateEncounter() {
  const qc = useQueryClient();
  return useMutation<CreateEncounterResponse, Error, CreateEncounterVariables>({
    mutationFn: async ({ appointment }) => {
      const patientId = findPatientParticipant(appointment.participant)?.patient?.id;
      const providerId = appointment.participant?.find((p) => {
        const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
        return t === 'practitioner' || t === 'provider';
      })?.provider?.id;

      if (!patientId || !providerId) {
        throw new Error(
          'Cannot create encounter — appointment is missing patient or provider context.',
        );
      }

      // Build the body in the EXACT wire shape verified by the dev
      // curl. Every nullable field ships present-and-null so the .NET
      // model binder doesn't reject a sparse body; arrays ship as `[]`
      // not null. Numeric / bool fields ship as their default value
      // (`0`, `false`).
      const body = {
        identifier: null,
        status: ENCOUNTER_STATUS_INITIAL,
        class: null,
        type: null,
        serviceType: null,
        subject: patientId,
        basedOn: null,
        appointment: appointment.id,
        startDate: null,
        endDate: null,
        createdDate: null,
        duration: 0,
        account: null,
        serviceProvider: providerId,
        parentEncounter: null,
        location: null,
        locationName: appointment.location?.name ?? null,
        isDeleted: false,
        reasonForVisit: appointment.reasonCode?.code ?? null,
        reasonForVisitName: appointment.reasonCode?.display ?? null,
        statusHistoryClass: [],
        historyClass: [],
        encounterLocationClass: [],
        speciality: null,
      };

      const raw = await postText('/api/Patients/CreateEncounter', body);
      // Strip surrounding quotes in case the backend ships the id as a
      // JSON string literal (`"<guid>"`) instead of bare text.
      const identifier = raw.trim().replace(/^"(.*)"$/, '$1');
      return { identifier };
    },
    onSuccess: (data, vars) => {
      // Race fix (Samer review PR #147 §3): write the new identifier
      // straight into the cache via setQueryData INSTEAD OF firing a
      // fire-and-forget invalidate. We have the id in `data` already
      // — no need to refetch.
      //
      // Why this matters: the caller does
      //   await createEncounter.mutateAsync(...)
      //   setAddOpen(true)
      // If onSuccess only invalidated, the refetch would still be
      // in-flight when the popup mounts and reads `encounterId`. With
      // setQueryData, the next render sees the new id synchronously —
      // no race, no orphan-encounter window.
      qc.setQueryData(
        ['encounter-by-appointment', vars.appointment.id],
        { identifier: data.identifier },
      );
    },
  });
}
